import React from "react";
import { motion } from "framer-motion";
import { UserCheck, Clock, IndianRupee, MapPin } from "lucide-react";

const points = [
  {
    icon: UserCheck,
    title: "Tax Consultant · GST & Income Tax",
    desc: "Experienced tax consultants specialising in GST and income tax audit, filing and compliance for Karnataka businesses.",
  },
  {
    icon: Clock,
    title: "Deadline-Driven",
    desc: "We track every GST and ITR due date so you never miss a filing or pay a late fee again.",
  },
  {
    icon: IndianRupee,
    title: "Genuinely Affordable",
    desc: "Transparent, fixed fees. No hidden charges. Packages built for small businesses and freelancers.",
  },
  {
    icon: MapPin,
    title: "Local to Tumakuru",
    desc: "Meet us in person, drop by the office, or work with us remotely — Karnataka-wide service.",
  },
];

export const About = () => {
  return (
    <section
      id="about"
      className="relative border-b border-border bg-secondary/30 py-24 md:py-32"
      data-testid="about-section"
    >
      <div className="container mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-16">
          <div className="md:col-span-6">
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden border border-border">
                <img
                  src="https://images.pexels.com/photos/7424584/pexels-photo-7424584.jpeg"
                  alt="NP Associates office"
                  className="h-full w-full object-cover"
                  loading="lazy"
                  data-testid="about-image"
                />
              </div>
              <div className="absolute -right-4 -top-4 hidden h-20 w-20 border border-foreground bg-primary md:block" />
              <div className="absolute bottom-6 left-6 border border-border bg-white p-4">
                <div className="font-[Outfit] text-3xl font-extrabold text-primary">10+</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Years serving Tumakuru
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-6">
            <div className="mb-4 flex items-center gap-3">
              <span className="h-px w-10 bg-primary" />
              <span className="font-[Outfit] text-xs font-bold uppercase tracking-[0.2em] text-primary">
                About NP Associates
              </span>
            </div>
            <h2 className="text-4xl font-extrabold leading-tight tracking-tight text-foreground md:text-5xl">
              Your neighbourhood CA firm.<br />
              <span className="italic text-primary">Built on trust.</span>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground md:text-lg">
              Founded in Tumakuru, NP Associates has grown into a trusted name
              for personal and business tax services across Karnataka. We believe
              professional financial services shouldn&apos;t be reserved for large
              corporations — every entrepreneur, freelancer and family deserves
              clear, honest advice.
            </p>

            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
              {points.map((p, i) => {
                const Icon = p.icon;
                return (
                  <motion.div
                    key={p.title}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.06 }}
                    className="border-l-2 border-primary bg-white p-5"
                    data-testid={`about-point-${i}`}
                  >
                    <Icon className="h-5 w-5 text-primary" />
                    <h3 className="mt-3 font-[Outfit] text-base font-bold text-foreground">
                      {p.title}
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {p.desc}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
